import { useTranslations } from 'use-intl';
import { naturalLanguageFormat } from '@monofront/business/utils';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import ReviewStars from '@monofront/design-system/components/ReviewStars';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import useI18n from '@hooks/i18n/useI18n';
import { useProductPageContext } from '@context/ProductPageContext';
import { SORT_PRODUCT_REVIEWS_BY } from '@monofront/business/constants';
import SelectList from '@monofront/design-system/components/SelectList';

const ProductReviewsList: React.FC = () => {
  const tReviews = useTranslations('Reviews');
  const { iso } = useI18n();
  const localeISO = iso.replace('_', '-');
  const { useGetProductReviewsHook } = useProductPageContext();

  if (!useGetProductReviewsHook) return null;

  const {
    productReviews: reviews,
    isLoading,
    seeMoreProductReviews,
    canSeeMoreProduct,
    displayedReviewsCount,
    totalReviewsCount,
    sortProductReviews,
    itemsPerPageCount,
  } = useGetProductReviewsHook;

  const reviewsLeftToSee = totalReviewsCount - displayedReviewsCount;

  if (isLoading) {
    return <HeartLoader />;
  }

  if (!reviews || reviews.length === 0) return null;

  return (
    <>
      <SelectList
        items={[
          {
            label: tReviews('sortBy', { sort: tReviews('dateDesc') }),
            value: SORT_PRODUCT_REVIEWS_BY.date_desc,
          },
          {
            label: tReviews('sortBy', { sort: tReviews('dateAsc') }),
            value: SORT_PRODUCT_REVIEWS_BY.date_asc,
          },
          {
            label: tReviews('sortBy', { sort: tReviews('rateDesc') }),
            value: SORT_PRODUCT_REVIEWS_BY.rate_desc,
          },
          {
            label: tReviews('sortBy', { sort: tReviews('rateAsc') }),
            value: SORT_PRODUCT_REVIEWS_BY.rate_asc,
          },
        ]}
        onChange={(e) => {
          e.preventDefault();
          sortProductReviews(e.target.value);
        }}
        UNSAFE_className="tw-mt-8 tw-mb-2"
      />

      {reviews.map((review) => (
        <article
          key={review.createdAt}
          className="tw-border-0 tw-border-b tw-border-solid tw-border-blue-200 tw-py-6"
        >
          <p className="tw-flex tw-justify-between">
            <span className="tw-flex tw-items-center tw-font-semibold">
              <ReviewStars
                averageRating={review.rating}
                size={IconSizes.md}
                UNSAFE_className="tw-mr-2 [&_svg]:tw-align-top"
              />
              {`${review.rating}/5`}
              <span className="tw-lf-label tw-lf-label-decorative-blue tw-ml-3 tw-h-[1.125rem]">
                {tReviews('verified')}
              </span>
            </span>
            <span className="tw-lf-legend-sm tw-pl-2 tw-text-right tw-leading-snug">
              {tReviews('date', {
                date: naturalLanguageFormat(review.createdAt, localeISO),
              })}
            </span>
          </p>
          <blockquote className="tw-mt-4 tw-leading-normal">
            {review.title.trim() && (
              <>
                {review.title}
                <br />
              </>
            )}
            {review.comment}
          </blockquote>
          <p className="tw-mt-4 tw-font-semibold">
            {!review.author.publicDisplayName ||
            review.author.publicDisplayName.includes('Customer')
              ? tReviews('anonymous')
              : review.author.publicDisplayName}
          </p>
        </article>
      ))}

      <p
        className={`tw-relative tw-mx-auto tw-mt-8 tw-w-fit
        after:tw-mt-2
        after:tw-block after:tw-h-[4px] after:tw-w-full after:tw-rounded-3xl after:tw-bg-beige-200 after:tw-content-['']`}
      >
        {tReviews('pagination', {
          seen: displayedReviewsCount,
          total: totalReviewsCount,
        })}
        <span
          className={`
          tw-bottom tw-absolute tw-mt-2
          tw-block tw-h-[4px] tw-rounded-3xl tw-bg-yellow-600
        `}
          style={{
            width: `${(displayedReviewsCount * 100) / totalReviewsCount}%`,
          }}
        ></span>
      </p>

      {canSeeMoreProduct && (
        <button
          type="button"
          className="tw-lf-button tw-lf-button-secondary tw-lf-button-md tw-mx-auto tw-mt-4"
          onClick={() => seeMoreProductReviews()}
        >
          {tReviews('seeMore', {
            count:
              reviewsLeftToSee > itemsPerPageCount
                ? itemsPerPageCount
                : reviewsLeftToSee,
          })}
        </button>
      )}
    </>
  );
};

export default ProductReviewsList;
